<script setup>
/**
 * GoalAgentTable
 * Displays agents progress table for a goal
 */
import { computed } from 'vue'
import {
  getGoalTableHeaders,
  formatAgentAmount,
  formatAgentDifference,
  isAgentMeetingGoal,
  formatGoalSummary,
} from '@/core/utils/goalHelpers'

const props = defineProps({
  goal: {
    type: Object,
    required: true,
  },
  showOnlyWithGoals: {
    type: Boolean,
    default: true,
  },
  highlightAgentId: {
    type: [String, Number],
    default: null,
  },
  showTutorial: {
    type: Boolean,
    default: false,
  },
})

function isHighlighted(agent) {
  if (!props.highlightAgentId) return false
  return String(agent.id) === String(props.highlightAgentId)
}

function getTutorialConfig(agent) {
  if (!props.showTutorial || !isHighlighted(agent)) return null
  return {
    id: 'statistics-agent-row',
    text: 'לחץ על שורת הסוכן שלך וצפה בפרטים נוספים על הביצוע של היעד',
    importance: 'info',
    showOnce: true,
    autoTrigger: false,
  }
}

const emit = defineEmits(['agent-click'])

function handleAgentClick(agent) {
  emit('agent-click', { agent, goal: props.goal })
}

function getAgentsToShow() {
  if (!props.goal.agents) return []

  if (props.showOnlyWithGoals) {
    return props.goal.agents.filter((agent) => agent.goal > 0)
  }

  return props.goal.agents
}
</script>

<template>
  <div class="agent-table-wrapper">
    <table class="agent-table">
      <thead>
        <tr>
          <th v-for="header in getGoalTableHeaders(goal.type)" :key="header">
            {{ header }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="agent in getAgentsToShow()"
          :key="agent.id"
          v-tutorial="getTutorialConfig(agent)"
          class="agent-row"
          :class="{ highlighted: isHighlighted(agent) }"
          @click="handleAgentClick(agent)"
        >
          <td class="agent-id">{{ agent.id }}</td>
          <td class="agent-name">{{ agent.name }}</td>
          <td class="agent-goal">{{ agent.goal }}</td>
          <td class="agent-achieved">{{ formatAgentAmount(agent, goal) }}</td>
          <td class="agent-diff" :class="{ positive: isAgentMeetingGoal(agent, goal) }">
            <span dir="ltr">{{ formatAgentDifference(agent, goal) }}</span>
          </td>
        </tr>

        <!-- Summary Row -->
        <tr class="summary-row">
          <td></td>
          <td>סיכום</td>
          <td>{{ formatGoalSummary(goal).goal }}</td>
          <td>{{ formatGoalSummary(goal).achieved }}</td>
          <td :class="{ positive: formatGoalSummary(goal).isAhead }">
            {{ formatGoalSummary(goal).difference }}
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="getAgentsToShow().length === 0" class="no-agents">
      <p>אין סוכנים עם יעדים</p>
    </div>
  </div>
</template>

<style scoped>
.agent-table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.agent-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.agent-table th,
.agent-table td {
  padding: var(--spacing-sm);
  text-align: center;
  border: 1px solid var(--border-color);
  white-space: nowrap;
}

.agent-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.agent-table th {
  background: var(--color-success);
  color: white;
  font-weight: var(--font-weight-semibold);
}

.agent-table tbody tr:nth-child(even) {
  background: var(--bg-secondary);
}

.agent-table tbody tr:nth-child(odd) {
  background: var(--bg-primary);
}

.agent-row {
  cursor: pointer;
  transition: background-color var(--transition-fast);
}

.agent-row:hover {
  background: var(--bg-tertiary) !important;
}

.agent-row.highlighted {
  background: linear-gradient(90deg, #fef3c7, #fde68a, #fef3c7) !important;
  font-weight: var(--font-weight-semibold);
}

.agent-row.highlighted:hover {
  background: linear-gradient(90deg, #fde68a, #fcd34d, #fde68a) !important;
}

.agent-name {
  text-align: right;
}

.agent-diff {
  color: var(--color-error);
  font-weight: var(--font-weight-medium);
}

.agent-diff.positive {
  color: var(--color-success);
}

.summary-row {
  background: rgba(16, 185, 129, 0.2) !important;
  font-weight: var(--font-weight-semibold);
}

.summary-row td.positive {
  color: var(--color-success);
}

.summary-row td:last-child:not(.positive) {
  color: var(--color-error);
}

.no-agents {
  padding: var(--spacing-lg);
  text-align: center;
  color: var(--text-muted);
}
</style>
